/**
 * Respawn13 — Product page
 *   - Image gallery (thumbnail click → swap main media)
 *   - Variant picker (radio change → update price + variant id + availability)
 *   - Quantity stepper
 *   - Add-to-cart (AJAX → opens cart drawer via window.Respawn.cart.add)
 */
(function () {
  'use strict';

  function init(root) {
    if (!root) return;

    var dataNode = root.querySelector('[data-product-json]');
    var product = null;
    if (dataNode) {
      try { product = JSON.parse(dataNode.textContent); } catch (e) { product = null; }
    }

    initGallery(root);
    initQty(root);
    initVariants(root, product);
    initSubmit(root);
    initTabs(root);
  }

  /* -----------------------------------------------------------------------
   * Product info tabs (Description / Details / Features / System Requirements)
   * Accessible: click + roving-tabindex arrow-key navigation (WAI-ARIA tabs).
   * --------------------------------------------------------------------- */
  function initTabs(root) {
    var wrap = root.querySelector('[data-pdp-tabs]');
    if (!wrap) return;

    var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[data-pdp-tab]'));
    var panels = Array.prototype.slice.call(wrap.querySelectorAll('[data-pdp-panel]'));
    if (!tabs.length) return;

    function activate(name, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-pdp-tab') === name;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panels.forEach(function (p) {
        var on = p.getAttribute('data-pdp-panel') === name;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        activate(tab.getAttribute('data-pdp-tab'), false);
      });
      tab.addEventListener('keydown', function (e) {
        var idx = i;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown': idx = (i + 1) % tabs.length; break;
          case 'ArrowLeft':
          case 'ArrowUp':   idx = (i - 1 + tabs.length) % tabs.length; break;
          case 'Home':      idx = 0; break;
          case 'End':       idx = tabs.length - 1; break;
          default: return;
        }
        e.preventDefault();
        activate(tabs[idx].getAttribute('data-pdp-tab'), true);
      });
    });
  }

  /* -----------------------------------------------------------------------
   * Gallery
   * --------------------------------------------------------------------- */
  function initGallery(root) {
    var thumbs = root.querySelectorAll('[data-media-target]');
    var slides = root.querySelectorAll('[data-media-id]');
    if (!thumbs.length || !slides.length) return;

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        var id = t.getAttribute('data-media-target');
        slides.forEach(function (s) {
          var match = s.getAttribute('data-media-id') === id;
          if (match) {
            s.hidden = false;
            s.classList.add('is-active');
          } else {
            s.hidden = true;
            s.classList.remove('is-active');
          }
        });
        thumbs.forEach(function (other) {
          other.classList.toggle('is-active', other === t);
        });
      });
    });
  }

  /* -----------------------------------------------------------------------
   * Quantity
   * --------------------------------------------------------------------- */
  function initQty(root) {
    var input = root.querySelector('[data-qty-input-pdp]');
    var up    = root.querySelector('[data-qty-up-pdp]');
    var down  = root.querySelector('[data-qty-down-pdp]');
    if (!input) return;

    if (up) up.addEventListener('click', function () {
      input.value = (parseInt(input.value, 10) || 1) + 1;
    });
    if (down) down.addEventListener('click', function () {
      var v = parseInt(input.value, 10) || 1;
      if (v > 1) input.value = v - 1;
    });
  }

  /* -----------------------------------------------------------------------
   * Variants
   * --------------------------------------------------------------------- */
  function initVariants(root, product) {
    if (!product || !product.variants) return;
    var inputs = root.querySelectorAll('[data-option-input]');
    var hiddenId = root.querySelector('[data-pdp-variant-id]');
    var submitBtn = root.querySelector('[data-pdp-submit]');
    var submitLabel = root.querySelector('[data-pdp-submit-label]');
    var priceWrap = root.querySelector('.pdp__price');
    if (!inputs.length || !hiddenId) return;

    function selected() {
      var values = [];
      var maxPos = 0;
      inputs.forEach(function (i) {
        var pos = parseInt(i.getAttribute('data-option-position'), 10);
        if (pos > maxPos) maxPos = pos;
      });
      for (var p = 1; p <= maxPos; p++) {
        var checked = root.querySelector('[data-option-input][data-option-position="' + p + '"]:checked');
        values.push(checked ? checked.value : null);
      }
      return values;
    }

    function arraysEqual(a, b) {
      if (a.length !== b.length) return false;
      for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    }

    function findMatch() {
      var current = selected();
      for (var i = 0; i < product.variants.length; i++) {
        if (arraysEqual(product.variants[i].options, current)) return product.variants[i];
      }
      return null;
    }

    function update() {
      var v = findMatch();
      if (!v) {
        submitBtn.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Unavailable';
        return;
      }
      hiddenId.value = v.id;
      submitBtn.disabled = !v.available;
      if (submitLabel) {
        submitLabel.textContent = v.available ? 'Add to Cart' : 'Sold Out';
      }
      // Update price text (use Shopify's money format if global available, else integer/100)
      if (priceWrap) {
        var current = v.price;
        var compare = v.compare_at_price;
        var formatted = formatMoney(current);
        var html = '<span class="price' + (compare && compare > current ? ' price--on-sale' : '') + '">' +
          '<span class="price__amount price__amount--current">' + formatted + '</span>';
        if (compare && compare > current) {
          html += '<s class="price__amount price__amount--compare">' + formatMoney(compare) + '</s>';
        }
        html += '</span>';
        priceWrap.innerHTML = html;
      }
      // Update URL with variant
      if (history && history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState(null, '', url.toString());
      }
    }

    function formatMoney(cents) {
      // Lightweight formatter — Shopify's full money_format is server-side.
      // Cents → $X.XX style. Multi-currency is fine because cart.currency.iso_code
      // is rendered server-side; this is just for variant change visual feedback.
      var amount = (cents / 100).toFixed(2);
      var symbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
      return symbol ? amount + ' ' + symbol : '$' + amount;
    }

    inputs.forEach(function (i) { i.addEventListener('change', update); });
  }

  /* -----------------------------------------------------------------------
   * Submit (AJAX add-to-cart → drawer)
   * --------------------------------------------------------------------- */
  function initSubmit(root) {
    var form = root.querySelector('[data-pdp-form]');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-pdp-submit]');
      var label = form.querySelector('[data-pdp-submit-label]');
      var origLabel = label ? label.textContent : '';
      if (btn) btn.disabled = true;
      if (label) label.textContent = 'Adding…';

      var variantId = form.querySelector('[name="id"]').value;
      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      var R = window.Respawn;
      if (!R || !R.cart) {
        // Fallback to default form submission
        form.submit();
        return;
      }
      R.cart.add(variantId, qty)
        .then(function () {
          if (label) label.textContent = 'Added';
          setTimeout(function () {
            if (btn) btn.disabled = false;
            if (label) label.textContent = origLabel;
          }, 1500);
        })
        .catch(function (err) {
          if (btn) btn.disabled = false;
          if (label) label.textContent = origLabel;
          if (R.toast) {
            R.toast(err && err.description || err && err.message || 'Could not add to cart', { kind: 'error' });
          } else {
            alert('Could not add to cart');
          }
        });
    });
  }

  /* -----------------------------------------------------------------------
   * Boot
   * --------------------------------------------------------------------- */
  function bootAll() {
    document.querySelectorAll('[data-product-section]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector) {
      e.target.querySelectorAll('[data-product-section]').forEach(init);
    }
  });
})();
